import React from 'react';
import { Box, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { colors, height, spacing, radius, FontFamily, typographyScale, TypographyVariant } from 'common/theme';
import { TextHot } from '../TextHot';
import { IconHot } from '../IconHot';

export const OptionSelectorHot = ({
  value,
  onChange,
  options = [],
  label,
  disabled = false,
  error = false,
  helperText,
  testID,
}) => {
  const theme = useTheme();

  const getOptionStyles = (isSelected) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    height: height.input,
    padding: `${spacing.md}px`,
    borderRadius: `${radius.md}px`,
    backgroundColor: isSelected ? colors.system.surface.subtle : colors.palette.white,
    border: `1px solid ${
      error
        ? theme.palette.error.main
        : isSelected
        ? colors.palette.blue550
        : colors.palette.blueAlpha200
    }`,
    cursor: disabled ? 'default' : 'pointer',
    transition: 'border-color 0.2s ease, background-color 0.2s ease',
    '&:hover': {
      borderColor: !disabled && colors.palette.blue550,
    },
  });

  return (
    <Box
      testId={testID}
      sx={{
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        gap: `${spacing.xs}px`,
        mb: { xs: `${spacing.xl}px`, md: 0 },
        ...(disabled && {
          filter: 'grayscale(100%)',
          opacity: 0.7,
        }),
      }}
    >
      {label && (
        <Typography
          sx={{
            fontFamily: FontFamily.regular,
            fontSize: typographyScale[TypographyVariant.BodyLarge].fontSize,
            lineHeight: typographyScale[TypographyVariant.BodyLarge].lineHeight,
            color: theme.palette.text.subtle,
          }}
        >
          {label}
        </Typography>
      )}

      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: `${spacing.md}px`,
        }}
      >
        {options.map((option) => {
          const isSelected = value === option.value;
          return (
            <Box
              key={option.value}
              onClick={() => !disabled && onChange?.(option.value)}
              sx={getOptionStyles(isSelected)}
            >
              {option.icon && (
                <IconHot 
                  src={option.icon} 
                  size={32} 
                  sx={{ ml: option.label ? `${spacing.sm}px` : 0 }} 
                />
              )}
              {option.label && (
                <TextHot 
                  size="l" 
                  color={isSelected ? 'primary' : 'subtle'}
                >
                  {option.label}
                </TextHot>
              )}
            </Box>
          );
        })}
      </Box>

      {helperText && (
        <Typography
          variant="caption"
          sx={{
            color: error ? theme.palette.error.main : theme.palette.text.subtle,
            display: 'block',
            marginTop: error ? `${spacing.xs}px` : 0,
          }}
        >
          {helperText}
        </Typography>
      )}
    </Box>
  );
};

export default OptionSelectorHot;